import { ReactNode } from "react";
import TextWithIcon from "common/Text/TextWithIcon";
import useNextOrder from "@shared/common/LiveComponents/useNextOrder";
import { SharedNextOrderProps } from "@shared/services/socket/types";
import { DOLLAR_COINS } from "@shared/common/constants";
import {
  formatSignedPercent,
  withThousands,
} from "features/bots/NewLook/newLookBotUtils";

export type NewLookNextOrderProps = Pick<
  SharedNextOrderProps,
  "symbol" | "exchange" | "nextOrder" | "orderType"
>;

/**
 * A live next order for the new look: its signed distance from the current
 * price (live from the prices socket) and its price ("$66,900", or with the
 * quote coin's icon for non-dollar pairs).
 */
const useNewLookNextOrder = ({
  symbol,
  exchange,
  nextOrder,
  orderType,
}: NewLookNextOrderProps) => {
  const { pair, priceText, priceProps, getDistance } = useNextOrder({
    symbol,
    exchange,
    nextOrder,
    orderType,
  });
  const distance = nextOrder ? Number(getDistance()) : undefined;
  const formattedPrice = withThousands(priceText);
  const price: ReactNode =
    !pair || DOLLAR_COINS.includes(pair) ? (
      `${pair ? "$" : ""}${formattedPrice}`
    ) : (
      <TextWithIcon
        {...priceProps}
        text={formattedPrice}
        iconStyle={PRICE_ICON_STYLE}
      />
    );
  return {
    distance,
    distanceText:
      distance === undefined ? undefined : formatSignedPercent(distance),
    price,
  };
};

export default useNewLookNextOrder;

const PRICE_ICON_STYLE = { width: 12, height: 12, marginRight: 4 };
